import React, { useState } from "react";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Department  } from '../../types/models';
 

interface Props {
  departments: Department[];
  selectedDepartment: Department | null;
  setSelectedDepartment: (d: Department | null) => void;
  fetchDepartments: (workspaceId: number) => void;
  fetchPositions: (departmentId: number) => void;
  workspaceId: number;
  showSnackbar: (message: string, severity: "success" | "error") => void;
  loading: boolean;
  setDeleteTarget: (item: { id: number; type: "department" }) => void;
  setDeleteDialogOpen: (open: boolean) => void;
  setCreateDialogOpen: () => void; // ✅ Changed to function
  setEditingItem: (item: { id: number; name: string; type: "department" }) => void;
}

const DepartmentTable: React.FC<Props> = ({
  departments,
  selectedDepartment,
  setSelectedDepartment,
  fetchPositions,
  loading,
  setDeleteTarget,
  setDeleteDialogOpen,
  setCreateDialogOpen,
  setEditingItem,
}) => {
  const [departmentSearch, setDepartmentSearch] = useState("");

  const filtered = departments.filter((d) =>
    d.name.toLowerCase().includes(departmentSearch.toLowerCase())
  );

  return (
    <Box sx={{ mb: 4 }}>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <TextField
          label="Search Departments"
          variant="outlined"
          size="small"
          value={departmentSearch}
          onChange={(e) => setDepartmentSearch(e.target.value)}
        />

        <Tooltip title="Add Department">
          <IconButton onClick={setCreateDialogOpen} color="primary">
            <span className="material-icons">add</span>
          </IconButton>
        </Tooltip>
      </Box>

      <TableContainer component={Paper}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((department) => (
                <TableRow
                  key={department.id}
                  hover
                  selected={selectedDepartment?.id === department.id}
                  onClick={() => {
                    setSelectedDepartment(department);
                    fetchPositions(department.id);
                  }}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>{department.name}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingItem({
                            id: department.id,
                            name: department.name,
                            type: "department",
                          });
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget({ id: department.id, type: "department" });
                          setDeleteDialogOpen(true);
                        }}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    No departments found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

export default DepartmentTable;
